import { ApostleLite } from '@apeiron/apostle'
import { useCurrencyContext } from '@apeiron/currency'
import {
  CARD_USD_OPTIONS,
  DEFAULT_ANIMATE_SHORT_DURATION,
  MayNull,
  noForwardProps
} from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import EthereumSVG from '@public/icons/icon-ethereum.svg'
import ApostleCard, {
  ApostleCardProps
} from '@src/components/apostles/card/ApostleCard'
import PriceText from '@src/components/share/generic/PriceText'
import usePriceCalculation from '@src/hooks/common/tools/usePriceCalculation'
import * as RA from 'ramda-adjunct'
import { FC, useMemo } from 'react'

const Card = styled(ApostleCard<ApostleLite>)`
  width: 160px;
  & {
    transition: transform ${DEFAULT_ANIMATE_SHORT_DURATION}ms ease-out;
  }
  :hover,
  :focus {
    transform: translateY(-5px);
  }
  :disabled {
    filter: grayscale(0.5) contrast(1.2);
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    width: 156px;
  }
`

const LabelOverlay = styled(Box, noForwardProps)<LabelOverlayProps>`
  background-color: #1b1b20;
  left: 50%;
  position: absolute;
  transform: translateX(-50%);
  width: 95.5%;
  ${props =>
    props.__smallCard
      ? `
        bottom: 3.5%;
        height: 15%;
      `
      : `
        bottom: 3%;
        height: 15%;
      `}
`

const Label = styled(Typography, noForwardProps)<LabelProps>`
  align-items: center;
  background: ${props =>
    props.__redStatus
      ? `
      linear-gradient(
        90deg,
        rgba(234, 67, 2, 0.2) 0%,
        rgba(234, 67, 2, 0.7) 25%,
        rgba(234, 67, 2, 1) 50%,
        rgba(234, 67, 2, 0.7) 75%,
        rgba(234, 67, 2, 0.2) 100%
      )
    `
      : `
      linear-gradient(
        90deg,
        rgba(0, 92, 215, 0) 0%,
        rgba(0, 92, 215, 0.7) 25%,
        rgba(0, 92, 215, 1) 50%,
        rgba(0, 92, 215, 0.7) 75%,
        rgba(0, 92, 215, 0) 100%
      )
    `};
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
  ${props =>
    props.__smallCard
      ? `
        font-size: 12px;
        font-weight: 500;
      `
      : `
        font-size: 16px;
        font-weight: 700;
      `}
`

const PriceTag = styled(Box)`
  bottom: 0%;
  left: 50%;
  position: absolute;
  text-align: center;
  transform: translate(-50%, -50%);
  width: 94%;
`

const PriceContainer = styled(Box)`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: 10px;
  height: 22px;
  width: 100%;
`

const EthPrice = styled(PriceText)`
  flex: 1.6;
  .Custom-Icon {
    ${props => props.theme.breakpoints.down('res1024')} {
      width: 16px;
      height: 16px;
    }
    margin-left: auto;
  }
  .Custom-Text {
    color: white;
    ${props => props.theme.breakpoints.up('res1024')} {
      font-size: 20px;
      font-weight: 400;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      font-size: 16px;
      font-weight: 400;
    }
  }
`

const FiatPrice = styled(PriceText)`
  flex: 1;
  padding-top: 3px;
  .Custom-Text {
    color: #999;
    ${props => props.theme.breakpoints.up('res1024')} {
      font-size: 14px;
      font-weight: 400;
    }
    ${props => props.theme.breakpoints.down('res1024')} {
      font-size: 12px;
      font-weight: 400;
    }
  }
`

const MarketplaceApostleCard: FC<MarketplaceApostleCardProps> = props => {
  const {
    apostle,
    statusText,
    redStatus,
    smallCard = false,
    overlay: inputOverlay,
    ...rest
  } = props

  const { getApeironPrice } = useCurrencyContext()

  const { ethPrice, usdPrice } = usePriceCalculation(apostle?.listingInfo)

  const showPrice = ethPrice > 0

  const overlay = useMemo(() => {
    if (RA.isNotNilOrEmpty(statusText)) {
      return (
        <LabelOverlay __smallCard={smallCard}>
          <Label __smallCard={smallCard} __redStatus={redStatus || false}>
            {statusText}
          </Label>
        </LabelOverlay>
      )
    }

    if (showPrice) {
      const formattedEthPrice = getApeironPrice(null, ethPrice)

      const formattedUsdPrice = getApeironPrice(
        null,
        usdPrice,
        CARD_USD_OPTIONS
      )

      return (
        <PriceTag>
          <PriceContainer>
            <EthPrice icon={EthereumSVG} displayPrice={formattedEthPrice} />
            <FiatPrice displayPrice={formattedUsdPrice} prefix='~$ ' />
          </PriceContainer>
        </PriceTag>
      )
    }

    return inputOverlay
  }, [
    ethPrice,
    inputOverlay,
    redStatus,
    showPrice,
    smallCard,
    statusText,
    usdPrice,
    getApeironPrice
  ])

  return (
    <Card
      {...rest}
      apostle={apostle}
      overlay={overlay}
      showInfo
      showName={!showPrice}
      smallCard={smallCard}
    />
  )
}

type LabelOverlayProps = {
  __smallCard: boolean
}

type LabelProps = LabelOverlayProps & {
  __redStatus: boolean
}

export type MarketplaceApostleCardProps = ApostleCardProps<ApostleLite> & {
  redStatus?: boolean
  smallCard?: boolean
  statusText?: MayNull<string>
}

export default MarketplaceApostleCard
